import {
  doc,
  getDoc,
  getDocs,
  collection,
  getCountFromServer,
  CollectionReference,
  query,
  limit,
  where,
  WhereFilterOp,
  FieldPath,
  orderBy,
  OrderByDirection,
  startAfter,
  endAt,
  addDoc,
  onSnapshot,
  startAt,
  QueryConstraint,
  DocumentData,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL } from "firebase/storage";
import { db, storage, cloud } from "../../_db";

export type WhereClause = [string | FieldPath, WhereFilterOp, string | number | boolean | string[] | number[]];

export type QueryCriteria = {
  id?: string;
  order?: OrderByDirection;
  orderBy?: string;
  start?: string;
  startAt?: string;
  endAt?: string;
  limit?: number;
  where?: WhereClause[];
};


/**
 * Creates a Firestore query based on the provided criteria
 * @param coll - The collection reference to query
 * @param criteria - Query criteria including ordering, filtering, and pagination
 * @returns A Firestore query with applied constraints
 */
function makeQuery(coll: CollectionReference, criteria: QueryCriteria = {}) {
  const q: QueryConstraint[] = [];
  if (criteria.orderBy) q.push(orderBy(criteria.orderBy, criteria.order || "asc"));
  if (criteria.where)
    for (const whre of criteria.where) {
      q.push(where(whre[0], whre[1], whre[2]));
    }
  if (criteria.start) q.push(startAfter(criteria.start));
  if (criteria.startAt) q.push(startAt(criteria.startAt));
  if (criteria.endAt) q.push(endAt(criteria.endAt));
  if (criteria.limit) q.push(limit(criteria.limit));
  return query(coll, ...q);
}

export interface FirestoreError {
  code: string;
  message: string;
}

const FirestoreService = {
  /**
   * Retrieves a single document from Firestore
   * @param entity - Collection name
   * @param query - Query criteria including document ID
   * @returns Document data or null if not found
   */
  get: async <T extends DocumentData>(entity: string, query?: QueryCriteria): Promise<T | null> => {
    try {
      const snap = await getDoc(doc(db, entity, query?.id || ""));
      return snap.exists() ? { id: snap.id, ...(snap.data() as T) } : null;
    } catch (error) {
      console.error(`Error fetching document from ${entity}:`, error);
      throw { code: "fetch-error", message: `Failed to fetch document from ${entity}` };
    }
  },

  /**
   * Retrieves multiple documents from Firestore with pagination
   * @param entity - Collection name
   * @param query - Query criteria for filtering and pagination
   * @returns Object containing total count and document array
   */
  getAll: async <T extends DocumentData>(entity: string, query?: QueryCriteria) => {
    try {
      const coll = collection(db, entity);
      const criteria = makeQuery(coll, query);
      const criteriaCount = makeQuery(coll, { ...query, limit: undefined, start: undefined });
      const snap = await getDocs(criteria);
      const total = (await getCountFromServer(criteriaCount)).data().count;
      const docs = snap.docs.map((doc) => ({ id: doc.id, ...(doc.data() as T) }));
      return { total, docs };
    } catch (error) {
      console.error(`Error fetching documents from ${entity}:`, error);
      throw { code: "fetch-error", message: `Failed to fetch documents from ${entity}` };
    }
  },

  /**
   * Creates a request document and listens for its status changes
   * @param action - The action to perform
   * @param body - Request payload
   * @param entity - Optional entity name
   * @param userId - User ID
   * @returns Promise that resolves with the request result
   */
  request: <T extends DocumentData>(action: string, body?: T, entity?: string, userId?: string) => {
    if (!userId) {
      return Promise.reject({
        code: "auth-error",
        message: "User not authenticated",
      });
    }

    return new Promise(async (resolve, reject) => {
      try {
        const cleanData = body ? Object.fromEntries(Object.entries(body).filter(([_, value]) => value !== "" && value !== undefined)) : {};

        const requestData = {
          entity: entity || "default",
          issuedBy: userId,
          action,
          data: cleanData,
          status: "pending",
          createdAt: new Date(),
        };

        const docRef = await addDoc(collection(db, "request"), requestData);

        const unsubscribe = onSnapshot(docRef, (doc) => {
          const data = doc.data();

          if (data?.status === "accepted") {
            unsubscribe();
            resolve(data || {});
          } else if (data?.status === "error" || data?.status === "failure") {
            unsubscribe();
            reject({
              code: data.error?.code || "request-error",
              message: data.error?.message || "Request failed",
            });
          }
        });

        setTimeout(() => {
          unsubscribe();
          reject({
            code: "timeout",
            message: "Request timed out",
          });
        }, 30000);
      } catch (error) {
        reject({
          code: "request-error",
          message: "Failed to create request",
        });
      }
    });
  },

  /**
   * Uploads a file to Firebase Storage
   * @param id - Document ID
   * @param entity - Entity name for storage path
   * @param body - Upload parameters including file and key
   * @returns Object containing download URL and filename
   */
  upload: async (
    id: string,
    entity: string,
    body: { key: string; file: File }
  ): Promise<{ url: string; fileName: string }> => {
    try {
      const fileName = body.key;
      const ref = storage.ref(`${entity}/${id}/${fileName}`);
      await storage.upload(ref, body.file);
      const url = await getDownloadURL(ref);
      return { url, fileName };
    } catch (error) {
      console.error("Error uploading file:", error);
      throw { code: "upload-error", message: "Failed to upload file" };
    }
  },

  /**
   * Calls a Firebase Cloud Function
   * @param data - Function parameters
   * @returns Promise that resolves with the function result
   */
  call: async <T, R>(functionName: string, data: T): Promise<R> => {
    try {
      const res = await httpsCallable<T, R>(cloud, functionName)(data);
      return res.data;
    } catch (error) {
      console.error("Error calling cloud function:", error);
      throw { code: "function-error", message: "Failed to call cloud function" };
    }
  },
};

export default FirestoreService;
